export type QuestCompletionData = {
  completed: boolean;
  latestCompletion?: string | Date;
  refreshPeriodHours?: number;
  validFrom?: string | Date;
};

export function isQuestCompletedForPeriod(
  questData: QuestCompletionData,
): boolean {
  const { completed, latestCompletion, refreshPeriodHours, validFrom } =
    questData;

  // If no refresh period is set and quest is completed, it's a one-time quest
  if (!refreshPeriodHours && completed) return true;

  // If no refresh period is set, quest can't be completed for a period
  if (!refreshPeriodHours) return false;

  // If no latest completion, quest hasn't been completed yet
  if (!latestCompletion) return false;

  const now = new Date();
  const latestCompletionDate = new Date(latestCompletion);
  const validFromDate = validFrom ? new Date(validFrom) : new Date(0);

  // Calculate the current period start based on validFrom and refreshPeriodHours
  const timeSinceValidFrom = now.getTime() - validFromDate.getTime();
  const periodsSinceValidFrom = Math.floor(
    timeSinceValidFrom / (refreshPeriodHours * 60 * 60 * 1000),
  );
  const currentPeriodStart = new Date(
    validFromDate.getTime() +
      periodsSinceValidFrom * refreshPeriodHours * 60 * 60 * 1000,
  );

  // Check if the latest completion was in the current period
  return latestCompletionDate >= currentPeriodStart;
}

/**
 * Gets the human-readable recurrence string for a quest
 */
export function getQuestRecurrenceString(refreshPeriodHours?: number): string {
  if (!refreshPeriodHours) return "";

  if (refreshPeriodHours === 24) {
    return "today";
  }
  if (refreshPeriodHours === 24 * 7) {
    return "this week";
  }
  if (refreshPeriodHours === 24 * 30) {
    return "this month";
  }

  // For other periods, use a generic message
  return "this period";
}

/**
 * Extended version that includes the recurrence string for UI display
 */
export function getQuestCompletionStatus(questData: QuestCompletionData) {
  const completedForPeriod = isQuestCompletedForPeriod(questData);
  const recurrenceString = getQuestRecurrenceString(
    questData.refreshPeriodHours,
  );

  return {
    completedForPeriod,
    recurrenceString,
  };
}
